<template>
	<v-dialog persistent width="auto" v-model="showDialog" v-if="showDialog">
		<v-card style="height: 90vh; width: 80vw; min-width: 600px" class="d-flex flex-column">
			<v-card-title class="card-header text-left fixed-card-title2">
				<v-row no-gutters>
					<v-col class="align-center d-flex" cols="10">
						<v-toolbar-title>{{ $t('upload-lab-using-ai') }}</v-toolbar-title>
					</v-col>
					<v-spacer />
					<v-icon @click="editMetadata = true" :disabled="!aiAnalysisMetadata" class="white-icon">mdi-pencil</v-icon>
					<v-icon @click="closeUploadDialog()" class="white-icon">mdi-close</v-icon>
				</v-row>
			</v-card-title>
			<v-card-text class="pa-4 flex-grow-1 d-flex flex-column overflow-y-auto">
				<input
					type="file"
					ref="fileInput"
					style="display: none"
					accept="application/pdf, image/png, image/jpg"
					@change="onFileSelected" />
				<v-alert v-if="fileError" type="error" dense dismissible @input="fileError = null">
					{{ fileError }}
				</v-alert>
				<v-row dense>
					<v-col cols="5" class="d-flex flex-column" style="height: 100%; overflow: hidden">
						<div class="flex-grow-1 d-flex flex-column justify-center align-center overflow-hidden" style="height: 0">
							<template v-if="previewUrl">
								<iframe
									v-if="fileType === 'application/pdf'"
									:src="`${previewUrl}#toolbar=0`"
									width="100%"
									height="100%"
									style="border: none; border-bottom: #000 1px solid; flex-grow: 1" />
								<v-img
									v-else
									id="image-to-upload"
									:src="previewUrl"
									contain
									width="100%"
									max-height="100%"
									style="flex-grow: 1" />
							</template>
							<div
								v-else
								class="grey--text flex-grow-1 d-flex flex-column align-center justify-center data-table-outline rounded"
								style="width: 100%">
								<v-icon size="64">mdi-file-find</v-icon>
								<p>{{ $t('no-file-selected') }}</p>
							</div>
						</div>
						<div class="mt-2 d-flex justify-space-between">
							<v-btn
								color="dark_purple"
								style="color: white; max-height: 36px; width: calc(50% - 4px)"
								:disabled="loadingAi"
								@click="$refs.fileInput.click()">
								{{ $t('upload-lab-document') }}
							</v-btn>
							<v-btn
								color="dark_purple"
								style="color: white; max-height: 36px; width: calc(50% - 4px)"
								:disabled="!selectedFile || loadingAi"
								@click="sendFileToAi()">
								<v-progress-circular v-if="loadingAi" indeterminate size="26" width="3" color="primary" />
								<span v-else>{{ $t('analyze-with-ai') }}</span>
							</v-btn>
						</div>
					</v-col>
					<v-col cols="7" class="d-flex flex-column" style="height: 100%; overflow: hidden">
						<div class="flex-grow-1" style="position: relative; height: 0; min-height: 0">
							<v-data-table
								:items="groupedData"
								:headers="headers"
								:items-per-page="-1"
								style="overflow-y: auto"
								item-key="panelGroup"
								:expanded.sync="expandedGroups"
								dense
								fixed-header
								hide-default-footer
								class="data-table-outline full-height-table">
								<template #item="{ item, index, expand, isExpanded }">
									<tr
										:class="[getRowsForDataTable(index), { 'expanded-row': isExpanded }]"
										@click="expand(!isExpanded)">
										<td class="text-left d-flex align-center">
											<v-icon small color="primary" class="mr-1">
												{{ isExpanded ? 'mdi-arrow-down-drop-circle' : 'mdi-arrow-right-drop-circle' }}
											</v-icon>
											{{ item.panelGroup }}
										</td>
										<td class="text-right">
											<v-icon
												color="primary"
												:disabled="loadingAi || !aiAnalysisMetadata?.labDate || panelGroupIsAdded(item)"
												@click.stop="addPanelGroup(item)">
												mdi-plus-circle
											</v-icon>
										</td>
									</tr>
								</template>
								<template #expanded-item="{ item }">
									<tr style="cursor: default; background-color: #fff !important">
										<td class="pa-3" :colspan="headers.length">
											<v-data-table
												:items="item.results"
												:headers="innerHeaders"
												dense
												:items-per-page="-1"
												hide-default-footer
												class="data-table-outline"
												fixed-header>
												<template #[`item.testName`]="{ item }">
													<v-tooltip left :open-delay="300">
														<template #activator="{ on, attrs }">
															<v-icon
																:color="item.isCritical ? 'primary' : 'green'"
																size="14"
																v-on="on"
																v-bind="attrs"
																:class="{ pulsing: item.isCritical }"
																class="mr-1">
																mdi-circle
															</v-icon>
														</template>
														{{ item.isCritical ? $t('is-critical') : $t('is-not-critical') }}
													</v-tooltip>
													{{ item.testName }}
												</template>
												<template #[`item.add`]="{ item }">
													<div class="d-flex">
														<v-icon color="primary" @click="editLab(item)"> mdi-pencil </v-icon>
														<v-icon
															color="primary"
															:disabled="loadingAi || addedLabs.includes(item.id) || !aiAnalysisMetadata?.labDate"
															@click="addLab(item)">
															mdi-plus-circle
														</v-icon>
													</div>
												</template>
											</v-data-table>
										</td>
									</tr>
								</template>
							</v-data-table>
						</div>
						<!-- prettier-ignore -->
						<div
								v-if="aiAnalysisMetadata"
								class="data-table-outline overflow-y-auto pa-2 text-left mt-2 rounded"
								style="height: 250px; white-space: pre-wrap"><b v-if="aiAnalysisMetadata.relatedDiagnosisCodeInICD10Format && aiAnalysisMetadata.formalDiagnosis">{{
								aiAnalysisMetadata.relatedDiagnosisCodeInICD10Format }}: {{ aiAnalysisMetadata.formalDiagnosis }}<br/><br/></b>{{ aiAnalysisMetadata.medicalEvaluationAnAdviseByAI }}</div>
						<div
							v-else-if="!loadingAi"
							class="data-table-outline rounded mt-2 d-flex flex-column align-center justify-center font-size18"
							style="height: 250px">
							{{ $t('no-file-analyzed') }}
						</div>
						<div
							v-else
							class="data-table-outline rounded mt-2 d-flex flex-column align-center justify-center font-size18"
							style="height: 250px">
							{{ $t('note') }}: {{ $t('analyzing-can-take-some-time-please-wait') }}
						</div>
					</v-col>
				</v-row>
				<v-navigation-drawer
					v-model="editMetadata"
					v-if="editMetadata"
					right
					stateless
					absolute
					temporary
					active-class="background-color: 'red'"
					:width="480"
					style="z-index: 2000">
					<v-card tile elevation="0" style="height: 100%; overflow-y: hidden">
						<v-card-title class="card-header text-left fixed-card-title2">
							<v-row no-gutters>
								<v-col class="align-center d-flex" cols="10">
									<v-toolbar-title>{{ $t('edit') }} {{ $t('metadata') }}</v-toolbar-title>
								</v-col>
								<v-spacer />
								<v-icon color="white" @click="editMetadata = false">mdi-close</v-icon>
							</v-row>
						</v-card-title>
						<v-card-text class="pt-4 overflow-y-auto" style="height: calc(100% - 62px)">
							<v-row dense class="ma-0">
								<v-col cols="12">
									<v-menu
										v-model="dateMenu"
										:close-on-content-click="false"
										transition="scale-transition"
										offset-y
										min-width="auto">
										<template #activator="{ on, attrs }">
											<v-text-field
												:value="formattedDate"
												:label="$t('lab-date')"
												prepend-inner-icon="mdi-calendar"
												readonly
												outlined
												dense
												hide-details
												v-bind="attrs"
												v-on="on" />
										</template>
										<v-date-picker v-model="aiAnalysisMetadata.labDate" no-title @input="dateMenu = false" />
									</v-menu>
								</v-col>
								<v-col cols="12">
									<v-text-field
										v-model="aiAnalysisMetadata.relatedDiagnosisCodeInICD10Format"
										:label="$t('related-diagnosis-code')"
										outlined
										dense
										hide-details />
								</v-col>
							</v-row>
						</v-card-text>
					</v-card>
				</v-navigation-drawer>
				<v-navigation-drawer
					v-model="editResult"
					v-if="editResult"
					right
					stateless
					absolute
					temporary
					active-class="background-color: 'red'"
					:width="480"
					style="z-index: 2000">
					<v-card tile elevation="0" style="height: 100%; overflow-y: hidden">
						<v-card-title class="card-header text-left fixed-card-title2">
							<v-row no-gutters>
								<v-col class="align-center d-flex" cols="10">
									<v-toolbar-title>{{ $t('edit') }} {{ $t('result') }}</v-toolbar-title>
								</v-col>
								<v-spacer />
								<v-icon color="white" @click="editResult = false">mdi-close</v-icon>
							</v-row>
						</v-card-title>
						<v-card-text class="pt-4 overflow-y-auto" style="height: calc(100% - 62px)">
							<v-row dense class="ma-0">
								<v-col cols="12">
									<v-text-field
										v-model="selectedResult.testName"
										:label="$t('test-name')"
										outlined
										dense
										hide-details />
								</v-col>
								<v-col cols="6">
									<v-text-field v-model="selectedResult.value" :label="$t('value')" outlined dense hide-details />
								</v-col>
								<v-col cols="6">
									<v-text-field v-model="selectedResult.unit" :label="$t('unit')" outlined dense hide-details />
								</v-col>
								<v-col cols="12">
									<v-text-field
										v-model="selectedResult.specimenType"
										:label="$t('specimen-type')"
										outlined
										dense
										hide-details />
								</v-col>
								<v-col cols="12">
									<v-text-field
										v-model="selectedResult.specimenType"
										:label="$t('specimen-type')"
										outlined
										dense
										hide-details />
								</v-col>
								<v-col cols="12">
									<v-text-field
										v-model="selectedResult.interpretation"
										:label="$t('interpretation')"
										outlined
										dense
										hide-details />
								</v-col>
								<v-col cols="12">
									<v-text-field
										v-model="selectedResult.labSource"
										:label="$t('lab-source')"
										outlined
										dense
										hide-details />
								</v-col>
								<v-col cols="12">
									<v-text-field
										v-model="selectedResult.panelGroup"
										:label="$t('panel-group')"
										outlined
										dense
										hide-details />
								</v-col>
								<v-col cols="12">
									<v-checkbox v-model="selectedResult.isCritical" :label="$t('is-critical')" dense hide-details />
								</v-col>
							</v-row>
						</v-card-text>
					</v-card>
				</v-navigation-drawer>
			</v-card-text>
		</v-card>
	</v-dialog>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { getRowsForDataTable, formatTimestamp } from '@/utils/utilities';
import html2pdf from 'html2pdf.js';
export default {
	name: 'UploadLabUsingAiDialog',
	props: { showDialog: { type: Boolean, default: false } },
	data () {
		return {
			previewUrl: null,
			fileType: null,
			selectedFile: null,
			fileError: null,
			loadingAi: false,
			editMetadata: false,
			dateMenu: false,
			editResult: false,
			selectedResult: null,
			aiAnalysisResults: [],
			aiAnalysisMetadata: null,
			expandedGroups: [],
			addedLabs: [],
		};
	},
	computed: {
		...mapState({
			selectedCCMRecord: (state) => state.ccmRecords.ccmRecord,
			userData: (state) => state.authentication.userData,
		}),
		headers () {
			return [
				{ text: this.$t('panel-group'), value: 'panelGroup' },
				{ text: '', sortable: false, value: 'add' },
			];
		},
		innerHeaders () {
			return [
				{
					text: this.$t('name'),
					sortable: true,
					value: 'testName',
					class: 'px-1',
					cellClass: 'px-1',
				},
				{
					text: this.$t('specimen-type'),
					sortable: true,
					value: 'specimenType',
					class: 'px-1',
					cellClass: 'px-1',
				},
				{
					text: this.$t('value'),
					sortable: false,
					value: 'value',
					class: 'px-1',
					cellClass: 'px-1',
				},
				{
					text: this.$t('unit'),
					sortable: false,
					value: 'unit',
					class: 'px-1',
					cellClass: 'px-1',
				},
				{
					text: this.$t('interpretation'),
					sortable: false,
					value: 'interpretation',
					class: 'px-1',
					cellClass: 'px-1',
				},
				{
					text: '',
					sortable: false,
					value: 'add',
					align: 'right',
					class: 'px-1',
					cellClass: 'px-1',
				},
			];
		},
		groupedData () {
			return this.aiAnalysisResults.reduce((groups, result) => {
				let group = groups.find((g) => g.panelGroup === result.panelGroup);
				if (!group) {
					group = { panelGroup: result.panelGroup, results: [] };
					groups.push(group);
				}
				group.results.push(result);
				return groups;
			}, []);
		},
		formattedDate () {
			if (!this.aiAnalysisMetadata) return '';
			return this.formatTimestamp(this.aiAnalysisMetadata.labDate);
		},
	},
	methods: {
		getRowsForDataTable,
		formatTimestamp,
		...mapActions({
			uploadLabDocumentToAi: 'ccmLabs/uploadLabDocumentToAi',
			addCCMLab: 'ccmLabs/addCCMLab',
		}),
		onFileSelected (event) {
			const file = event.target.files[0];
			if (!file) return;
			if (file.size > 1024000) {
				this.fileError = this.$t('file-too-large-max-1mb');
				this.clearPreview();
				return;
			}
			this.fileError = null;
			this.selectedFile = file;
			this.fileType = file.type;
			this.previewUrl = URL.createObjectURL(file);
		},
		closeUploadDialog () {
			this.clearPreview();
			this.$emit('closeDialog');
		},
		clearPreview () {
			if (this.previewUrl) {
				URL.revokeObjectURL(this.previewUrl);
			}
			this.previewUrl = null;
			this.fileType = null;
			this.selectedFile = null;
			if (this.$refs.fileInput) this.$refs.fileInput.value = '';
		},
		async sendFileToAi () {
			this.loadingAi = true;
			const request = new FormData();
			request.append('CcmRecordId', this.selectedCCMRecord.id);
			request.append('PatientId', this.selectedCCMRecord.patientId);
			request.append('HospitalId', this.selectedCCMRecord.hospitalId);
			request.append('DocumentType', 'pdf');
			if (this.fileType.split('/')[0] === 'image') {
				const element = document.getElementById('image-to-upload');
				const pdfBlob = await html2pdf()
					.set({
						margin: 1,
						image: { type: 'png', quality: 0.98 },
						html2canvas: {
							scale: 2,
							logging: false,
							useCORS: true,
							allowTaint: true,
						},
						jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
					})
					.from(element)
					.toPdf()
					.get('pdf')
					.outputPdf('blob');
				const newFileName = this.selectedFile.name.replace(/\.[^/.]+$/, '') + '.pdf';
				request.append('File', pdfBlob, newFileName);
			} else {
				const fileBlob = new Blob([this.selectedFile], { type: this.fileType });
				request.append('File', fileBlob, this.selectedFile.name);
			}
			request.append('StaffId', this.userData.id);
			request.append('UploadedBy', `${this.userData.firstName} ${this.userData.lastName}`);
			request.append('Notes', '');
			request.append('TimeSpentSeconds', 0);
			request.append('AdditionalData', '');
			try {
				const response = await this.uploadLabDocumentToAi(request);
				this.addedLabs = [];
				this.aiAnalysisResults = response.data.labTestResult.map((r, idx) => {
					return { ...r, id: idx };
				});
				this.aiAnalysisMetadata = response.data.labMetaData;
				if (this.aiAnalysisMetadata.labDate === null) {
					this.aiAnalysisMetadata.labDate = new Date();
				}
				if (this.groupedData && this.groupedData.length > 0) {
					this.expandedGroups = this.groupedData.map((g) => g.panelGroup);
				}
			} finally {
				this.loadingAi = false;
			}
		},
		async addLab (result) {
			this.loadingAi = true;
			const body = {
				ccmRecordId: this.selectedCCMRecord.id,
				patientId: this.selectedCCMRecord.patientId,
				hospitalId: this.selectedCCMRecord.hospitalId,
				labDate: this.aiAnalysisMetadata.labDate,
				testName: result.testName,
				value: result.value,
				unit: result.unit ?? '',
				referenceRange: result.referenceRange,
				interpretation: result.interpretation,
				specimenType: result.specimenType,
				isCritical: result.isCritical,
				labSource: result.labSource,
				panelGroup: result.panelGroup,
				staffId: this.userData.id,
				performedByRole: this.userData.roleData.roleName,
				reviewedBy: `${this.userData.firstName} ${this.userData.lastName}`,
				reviewedAt: new Date().toISOString(),
				timeSpentSeconds: this.aiAnalysisMetadata.timeSpentByAIToProcessTheDocumentInSeconds,
				relatedDiagnosisCode: this.aiAnalysisMetadata.relatedDiagnosisCodeInICD10Format,
				additionalData: '',
			};
			await this.addCCMLab(body);
			this.addedLabs.push(result.id);
			this.loadingAi = false;
		},
		addPanelGroup (panelGroup) {
			panelGroup.results.forEach((result) => {
				if (!this.addedLabs.includes(result.id)) {
					this.addLab(result);
				}
			});
		},
		editLab (result) {
			this.selectedResult = result;
			this.editResult = true;
		},
		panelGroupIsAdded (panelGroup) {
			return panelGroup.results.every((result) => this.addedLabs.includes(result.id));
		},
	},
};
</script>

<style lang="scss" scoped>
.full-height-table {
	display: flex;
	flex-direction: column;
	height: 100%;
}
.full-height-table :deep(.v-data-table__wrapper) {
	flex: 1 1 auto;
}
@keyframes pulse-size {
	0% {
		transform: scale(1.2);
	}
	50% {
		transform: scale(0.75);
	}
	100% {
		transform: scale(1.2);
	}
}
.pulsing {
	animation: pulse-size 1s ease-in-out infinite;
	display: inline-block;
	transform-origin: center;
}
</style>
